import React from 'react'
import {Box, Stack, Typography} from '@mui/material'

import {
  ChoroplethScale,
  CHOROPLETH_FILL_ALPHA,
  CHOROPLETH_INSUFFICIENT_ALPHA,
  CHOROPLETH_INSUFFICIENT_COLOR,
  formatChoroplethValue,
  rgba,
} from './choropleth'
import {MapChoroplethKind} from './types'

/**
 * ── THE CHOROPLETH'S LEGEND (VERA-260) ──────────────────────────────────────────────
 *
 * **A choropleth without a legend is undecodable.** That is not a style preference: the
 * entire encoding is colour → magnitude, and with no key the reader has a picture of
 * relative darkness and no way to convert it into a number, a threshold, or a comparison
 * against anything outside this one card. So this is not optional chrome — it is half the
 * chart, and the map renders it itself rather than leaving it to a consumer who might not.
 *
 * It prints the BAND BOUNDARIES, not just a "low → high" gradient bar. A bare gradient
 * tells a reader that one country is darker than another, which they could already see;
 * the numbers are what let them say "this country is above 4%". They also make the
 * scale's known weakness legible — see `buildChoroplethScale` on why linear-from-zero
 * compresses a skewed distribution, and note that a reader can SEE that compression here
 * because the band widths are printed.
 *
 * ⚠️ Every value it renders comes from the same `ChoroplethScale` the fill is computed
 * from (`choropleth.ts`). A legend built from its own independent pass over the data is a
 * legend that will eventually describe a scale the map is not using — and a confidently
 * wrong key is worse than none.
 */
export const MapChoroplethLegend: React.FC<{
  label: string
  kind: MapChoroplethKind
  scale: ChoroplethScale
  /** Copy for the small-sample swatch, e.g. "Fewer than 30 scans". Omitted when the
   *  threshold suppressed nothing — a key to a colour that is not on screen is noise. */
  insufficientLabel?: string
  /** What the map could not account for. See `MapChoroplethConfig.footnote`. */
  footnote?: string
}> = ({label, kind, scale, insufficientLabel, footnote}) => {
  const showInsufficient = Boolean(
    insufficientLabel && scale.insufficientCount > 0,
  )

  /*
    ⚠️ NO VALUE BANDS WHEN NOTHING IS COLOURED BY THEM.

    When the threshold suppresses every row, the scale falls back to a nominal 0–100%
    (`buildChoroplethScale`) and this printed a full six-band key for six colours that
    were nowhere on the map — a key to an encoding not in use, sitting above the one
    swatch that actually explained the screen. Observed live: on a three-day window the
    repeat-scan rate suppressed all fifteen countries, and the legend still advertised
    "83.3% – 100.0%" beside an entirely grey world.

    Nothing coloured is a real state with a real explanation, and the explanation is the
    insufficient row. Show that alone.
  */
  const showBands = scale.coloredCount > 0

  return (
    <Box
      sx={(theme) => ({
        // Absolutely positioned by the map's own wrapper, bottom-left. It can share that
        // corner with nothing else in this mode: `renderOverlay` draws only for a
        // SELECTED item and choropleth mode has no markers to select.
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary?.[100] ?? theme.palette.divider}`,
        // 10px, matching the map's zoom/layer controls and every button in the app.
        borderRadius: '10px',
        boxShadow: theme.shadows[2],
        // ABSOLUTE px, never spacing factors — working-conventions §47. This is a LAYER-1
        // component and the two apps have different spacing bases (@repo/ui 4px, apps/app
        // MUI's default 8px), so a `p: 2` here would render at double size in one of them.
        padding: '10px 12px',
        maxWidth: 'calc(100% - 32px)',
        pointerEvents: 'none',
      })}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 600,
          color: 'text.primary',
          marginBottom: '6px',
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>

      <Stack spacing={0} sx={{gap: '3px'}}>
        {showBands &&
          scale.bands.map((band, i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            sx={{gap: '8px'}}
          >
            <Box
              sx={{
                width: 14,
                height: 10,
                borderRadius: '2px',
                flexShrink: 0,
                backgroundColor: rgba(band.color, CHOROPLETH_FILL_ALPHA),
              }}
            />
            <Typography
              variant="caption"
              sx={{color: 'text.secondary', lineHeight: 1.3, whiteSpace: 'nowrap'}}
            >
              {/*
                The BOTTOM band prints as "< x" rather than "0 – x". Its true lower bound
                is exactly zero, and "0.0% – 0.8%" invites the reading that a country in
                it might be at zero — which for an invalid-rate map is the difference
                between "clean" and "nearly clean". "< 0.8%" says only what is true.
              */}
              {i === 0
                ? `< ${formatChoroplethValue(band.to, kind)}`
                : `${formatChoroplethValue(band.from, kind)} – ${formatChoroplethValue(band.to, kind)}`}
              </Typography>
            </Stack>
          ))}

        {showInsufficient && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{gap: '8px', marginTop: '4px'}}
          >
            <Box
              sx={{
                width: 14,
                height: 10,
                borderRadius: '2px',
                flexShrink: 0,
                backgroundColor: rgba(
                  CHOROPLETH_INSUFFICIENT_COLOR,
                  CHOROPLETH_INSUFFICIENT_ALPHA,
                ),
              }}
            />
            <Typography
              variant="caption"
              sx={{color: 'text.secondary', lineHeight: 1.3, whiteSpace: 'nowrap'}}
            >
              {insufficientLabel}
              {` (${scale.insufficientCount})`}
            </Typography>
          </Stack>
        )}
      </Stack>

      {footnote && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            marginTop: '8px',
            lineHeight: 1.4,
            // The footnote is the only line here that can genuinely be long (it names
            // unresolved country strings), so it is the one that wraps.
            whiteSpace: 'normal',
            maxWidth: 220,
          }}
        >
          {footnote}
        </Typography>
      )}
    </Box>
  )
}
